// src/components/CtaBar/CtaBar.js
import React, { useState } from "react";
import { IonModal, IonButton, IonIcon } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { useHistory, useLocation } from "react-router";
import { redirectToPremium } from "../../utils/premiumRedirect";
import "./CtaBar.css";
import CancelTrialConfirmModal from "./CancelTrialConfirmModal";

/**
 * normalizeCta — maps the backend's flat cta_* keys to the internal shape
 * used by CtaPopup and the global upgrade banner.
 *
 * API shape (from course endpoint):
 *   cta.cta_text                string
 *   cta.cta_popup_button_text   string
 *   cta.cta_popup_button_url    string   (e.g. "/shop") — may be auto-filled
 *                                          by the backend with a direct-checkout
 *                                          URL when the CRM field is empty
 *   cta.cta_configured_button_url string  what the CRM actually holds, empty when
 *                                          nobody set it; the red banner uses this
 *   cta.cta_popup_image_url     string   (full URL or null)
 *   cta.visible_from_lesson_number  string|number
 *
 * Internal shape:
 *   { text, popup: { button_text, button_url, image_url } }
 *
 * Returns null if the input is null/undefined.
 */
export function normalizeCta(raw) {
  if (!raw) return null;
  // Already normalized (has .text key) — pass through
  if (raw.text !== undefined) return raw;
  return {
    text: raw.cta_text ?? null,
    visibleFromLesson: raw.visible_from_lesson_number
      ? Number(raw.visible_from_lesson_number)
      : null,
    popup: {
      image_url: raw.cta_popup_image_url ?? null,
      button_text: raw.cta_popup_button_text ?? null,
      button_url: raw.cta_popup_button_url ?? null,
      // Only set when a person typed it into the CRM — never the backend's
      // direct-checkout fallback. Kept separate so an empty field means "leave
      // the banner alone" rather than "send everyone to a checkout".
      configured_button_url: raw.cta_configured_button_url ?? null,
    },
  };
}

/* ─────────────────────────────────────────────────────────────── */

export function CtaPopup({ isOpen, onClose, cta }) {
  const history = useHistory();
  const location = useLocation();
  const normalized = normalizeCta(cta);
  if (!normalized) return null;

  const { popup } = normalized;

  const handleCtaBtn = async () => {
    onClose();
    // This popup is the end-of-Percorso-Free "go premium" moment.
    // Route through the shared premium helper so the destination is
    // consistent with the burger menu's "Passa a Premium" entry — on
    // native that's always /paywall (Apple/Google IAP requirement);
    // on web it resolves the active pre-register widget. The backend's
    // `cta_popup_button_url` is intentionally bypassed here.
    await redirectToPremium({ history, pathname: location.pathname });
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="cta-popup-modal"
    >
      <div className="cta-popup">
        {/* X close */}
        <button
          className="cta-popup__close"
          onClick={onClose}
          aria-label="Chiudi"
        >
          <IonIcon icon={closeOutline} />
        </button>

        {/* Header illustration */}
        <div className="cta-popup__hero">
          {popup?.image_url ? (
            <img
              src={popup.image_url}
              alt=""
              className="cta-popup__hero-img"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="cta-popup__trophy" aria-hidden="true">
              🏆
            </div>
          )}
        </div>

        {/* Title — hidden when popup image is present */}
        {/* {!popup?.image_url && normalized.text && (
          <h2 className="cta-popup__title">{normalized.text}</h2>
        )} */}

        {/* CTA button */}
        {popup?.button_text && (
          <IonButton
            expand="block"
            color="primary"
            className="cta-popup__btn"
            onClick={handleCtaBtn}
          >
            {popup.button_text}
          </IonButton>
        )}
      </div>
    </IonModal>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/** Trial status uses the backend cancellation decision, not module progress. */
export function CtaBar({ isFreeUser, canCancel, onCancel }) {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  if (!isFreeUser) return null;

  return (
    <div className="cta-bar cta-bar--trial">
      <p className="cta-bar__trial-text">
        <strong>Prova gratuita attiva.</strong>
        {canCancel === true && (
          <>
            {" → "}
            <button
              type="button"
              className="cta-bar__trial-cancel"
              onClick={() => setShowCancelConfirm(true)}
            >
              Annulla la prova
            </button>
          </>
        )}
      </p>
      {canCancel === true && (
        <CancelTrialConfirmModal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={onCancel}
        />
      )}
    </div>
  );
}

export default CtaBar;
